import { Card, IconButton, TableFooter, TableHead } from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import EditIcon from "@material-ui/icons/Edit";
import ConfirmDialog from "@saleor/autobooking/components/ConfirmDialog";
import ButtonActions from "@saleor/components/ButtonActions";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePagination from "@saleor/components/TablePagination/TablePagination";
import useNotifier from "@saleor/hooks/useNotifier";
import { maybe, renderCollection } from "@saleor/misc";
import { useTableStyles } from "@saleor/remittanceRunner/styles/tableStyles";
import {
  useEnableAllMallRemittance,
  useSetDailyRemittanceConfig
} from "@saleor/riderRemittanceConfig/mutations";
import {
  RemittanceMallsConfig,
  RemittanceMallsConfig_malls_edges_node,
  RemittanceMallsConfigVariables
} from "@saleor/riderRemittanceConfig/types/RemittanceMallsConfig";
import { ListProps } from "@saleor/types";
import { currencyFormat } from "@saleor/utils/formatters/currencyFormat";
import { ApolloQueryResult } from "apollo-client";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ReminderDialog from "../ReminderDialog/ReminderDialog";

interface ReminderListProps extends ListProps {
  mallList: RemittanceMallsConfig_malls_edges_node[];
  refetch: (
    variables?: RemittanceMallsConfigVariables
  ) => Promise<ApolloQueryResult<RemittanceMallsConfig>>;
}

const numberOfColumns = 6;

const ReminderList: React.FC<ReminderListProps> = props => {
  const {
    mallList,
    refetch,
    disabled,
    settings,
    pageInfo,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings
  } = props;

  const tblClasses = useTableStyles(props);

  const intl = useIntl();
  const notify = useNotifier();
  const [openModal, setOpenModal] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [bulkActionText, setBulkActionText] = useState("");
  const [bulkActionEnable, setBulkActionEnable] = useState(null);
  const [bulkActionModalOpen, setBulkActionModalOpen] = useState(false);

  const notifTimesDisplay = strArray => {
    const myArray: string[] = strArray;

    if (myArray.length > 3) {
      const firstThreeElements = myArray.slice(0, 3).join(", ");
      return `${firstThreeElements}, ...`;
    } else {
      return myArray.join(", ");
    }
  };

  const editReminder = data => {
    setSelectedReminder(data);
    setOpenModal(true);
    setBulkActionText("");
  };
  const closeModal = () => {
    setSelectedReminder(null);
    setOpenModal(false);
    setBulkActionText("");
  };

  const handleError = errors => {
    const [error] = errors.graphQLErrors;
    notify({
      status: "error",
      text: error?.message
    });
  };

  const [remittanceConfig] = useSetDailyRemittanceConfig({
    onCompleted: data => {
      if (data?.setDailyRemittanceConfig?.errors?.length === 0) {
        notify({
          autohide: 5000,
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Changes saved!"
          })
        });
        closeModal();
        refetch();
      } else if (data?.setDailyRemittanceConfig?.errors?.length > 0) {
        data.setDailyRemittanceConfig.errors.forEach(error => {
          notify({
            status: "error",
            text: error.message
          });
        });
      }
    },
    onError: handleError
  });

  const bulkUpdateRemittance = enable => {
    enableAllMall({
      variables: {
        enable
      }
    });
  };

  const [enableAllMall] = useEnableAllMallRemittance({
    onCompleted: data => {
      if (data?.enableAllMallRemittance?.errors?.length === 0) {
        notify({
          status: "success",
          text: "Changes saved!"
        });
        handleBulkUpdateModalOpen(false);
        refetch();
      } else if (data?.enableAllMallRemittance?.errors?.length > 0) {
        data.enableAllMallRemittance.errors.forEach(error => {
          notify({
            status: "error",
            text: error.message
          });
        });
      } else {
        notify({
          status: "error",
          text: "Something went wrong. Please try again later."
        });
      }
    },
    onError: handleError
  });

  const enabledMallCount = mallList
    ? mallList.filter(mall => mall.remittanceConfig?.isEnable === true).length
    : 0;

  const disabledMallCount = mallList
    ? mallList.filter(mall => mall.remittanceConfig?.isEnable === false).length
    : 0;

  const handleBulkUpdateModalOpen = (
    forOpen: boolean,
    forEnable: boolean = null
  ) => {
    if (forOpen) {
      setBulkActionText(forEnable ? "enable" : "disable");
      setBulkActionEnable(forEnable);
    } else {
      setBulkActionText("");
      setBulkActionEnable(null);
    }

    setBulkActionModalOpen(forOpen);
  };

  const handleBulkUpdateModalConfirm = (forBulkEnable: boolean) => {
    bulkUpdateRemittance(forBulkEnable);
  };

  return (
    <>
      <Card>
        <div className={tblClasses.content}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCellHeader
                  colSpan={numberOfColumns - 3}
                  className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus} ${tblClasses.tableHeaderTitle}`}
                >
                  <FormattedMessage
                    defaultMessage="List of Reminders"
                    description="List of Reminders"
                  />
                </TableCellHeader>
                <TableCellHeader
                  colSpan={3}
                  className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus} ${tblClasses.tableHeaderTitle}`}
                  textAlign="right"
                >
                  <ButtonActions
                    id={"remittanceBulkActions"}
                    btnText={"Bulk Actions"}
                    items={[
                      {
                        action: () => handleBulkUpdateModalOpen(true, false),
                        disabled: enabledMallCount === 0,
                        id: "remittanceBulkActionsDeactivate",
                        label: "Disable Remittance"
                      },
                      {
                        action: () => handleBulkUpdateModalOpen(true, true),
                        disabled: disabledMallCount === 0,
                        id: "remittanceBulkActionsActivate",
                        label: "Enable Remittance"
                      }
                    ]}
                  />
                </TableCellHeader>
              </TableRow>
              <TableRow>
                <TableCellHeader
                  className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
                >
                  <FormattedMessage defaultMessage="Mall" description="mall" />
                </TableCellHeader>
                <TableCellHeader
                  textAlign="center"
                  className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
                >
                  Reminder Time
                </TableCellHeader>
                <TableCellHeader
                  textAlign="center"
                  className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
                >
                  COD Remittance Limit
                </TableCellHeader>
                <TableCellHeader
                  textAlign="center"
                  className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
                >
                  Min. Remittance Amount
                </TableCellHeader>
                <TableCellHeader
                  className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
                >
                  Activate
                </TableCellHeader>
                <TableCellHeader
                  className={`${tblClasses.tableCellHeader} ${tblClasses.colStatus}`}
                >
                  <FormattedMessage defaultMessage="Edit" description="edit" />
                </TableCellHeader>
              </TableRow>
            </TableHead>
            <TableFooter>
              <TableRow>
                <TablePagination
                  colSpan={numberOfColumns}
                  settings={settings}
                  hasNextPage={
                    pageInfo && !disabled ? pageInfo.hasNextPage : false
                  }
                  onNextPage={onNextPage}
                  onUpdateListSettings={onUpdateListSettings}
                  hasPreviousPage={
                    pageInfo && !disabled ? pageInfo.hasPreviousPage : false
                  }
                  onPreviousPage={onPreviousPage}
                />
              </TableRow>
            </TableFooter>
            <TableBody>
              {renderCollection(
                mallList,
                mall => (
                  <TableRow key={mall ? mall.id : "skeleton"}>
                    <TableCell
                      className={`${tblClasses.tableCellBody} ${tblClasses.colStatus}`}
                    >
                      <div>
                        <div className={tblClasses.dataTitle}>Mall</div>
                        <div className={tblClasses.dataDisplay}>
                          {maybe(() => mall.name) ? (
                            <span>{mall.name}</span>
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      className={`${tblClasses.tableCellBody} ${tblClasses.colStatus}`}
                    >
                      <div className={tblClasses.dataGroup}>
                        <div className={tblClasses.dataTitle}>
                          Reminder Time
                        </div>
                        <div
                          className={`${tblClasses.dataDisplay} ${tblClasses.textCenter}`}
                        >
                          {maybe(
                            () => mall.remittanceConfig.dailyNotifTimes
                          ) ? (
                            <span>
                              {notifTimesDisplay(
                                mall.remittanceConfig.dailyNotifTimes
                              )}
                            </span>
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      className={`${tblClasses.tableCellBody} ${tblClasses.colStatus}`}
                    >
                      <div className={tblClasses.dataGroup}>
                        <div className={tblClasses.dataTitle}>
                          COD Remittance Limit
                        </div>
                        <div
                          className={`${tblClasses.dataDisplay} ${tblClasses.textCenter}`}
                        >
                          {maybe(() => mall.remittanceConfig) ? (
                            <span>
                              {currencyFormat(
                                mall.remittanceConfig.codLimit,
                                false
                              )}
                            </span>
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      className={`${tblClasses.tableCellBody} ${tblClasses.colStatus}`}
                    >
                      <div className={tblClasses.dataGroup}>
                        <div className={tblClasses.dataTitle}>
                          Min. Remittance Amount
                        </div>
                        <div
                          className={`${tblClasses.dataDisplay} ${tblClasses.textCenter}`}
                        >
                          {maybe(() => mall.remittanceConfig) ? (
                            <>
                              {mall?.remittanceConfig?.minRemittanceLimit >
                              0 ? (
                                <span>
                                  {currencyFormat(
                                    mall.remittanceConfig.minRemittanceLimit,
                                    false
                                  )}
                                </span>
                              ) : (
                                <span>-</span>
                              )}
                            </>
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      className={`${tblClasses.tableCellBody} ${tblClasses.colStatus}`}
                    >
                      <div className={tblClasses.dataGroup}>
                        <div className={tblClasses.dataTitle}>Status</div>
                        <div
                          className={`${tblClasses.dataDisplay} ${tblClasses.textCenter}`}
                        >
                          {maybe(() => mall.remittanceConfig) ? (
                            <>
                              {mall?.remittanceConfig?.isEnable ? (
                                <span
                                  style={{ color: "#00C6A2", fontWeight: 600 }}
                                >
                                  ACTIVE
                                </span>
                              ) : (
                                <span
                                  style={{ color: "#747C8C", fontWeight: 600 }}
                                >
                                  INACTIVE
                                </span>
                              )}
                            </>
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      className={`${tblClasses.tableCellBody} ${tblClasses.colStatus}`}
                    >
                      <div className={tblClasses.dataGroup}>
                        <div className={tblClasses.dataTitle}>Edit</div>
                        <div className={tblClasses.dataDisplay}>
                          <IconButton
                            color="primary"
                            onClick={() => {
                              editReminder(mall);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ),
                () => (
                  <TableRow>
                    <TableCell colSpan={numberOfColumns}>
                      {"No records found"}
                    </TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <ReminderDialog
        mall={selectedReminder || null}
        open={openModal}
        onClose={closeModal}
        onSubmit={data =>
          remittanceConfig({
            variables: data
          })
        }
      />

      <ConfirmDialog
        open={bulkActionModalOpen}
        onClose={() => handleBulkUpdateModalOpen(false)}
        modalTitle={
          "You are about to " +
          bulkActionText +
          " remittances for some SM Malls."
        }
        modalText={
          bulkActionEnable
            ? "When enabled, remittance will automatically be accounted for in all future orders assigned to the rider."
            : "When disabled, no remittance will be applied to future orders assigned to the rider."
        }
        confirmLabel={bulkActionEnable ? "ENABLE" : "DISABLE"}
        modalTitleTxtClass={tblClasses.bulkActionModalTitleText}
        confirmLabelBtnClass={
          !bulkActionEnable ? tblClasses.bulkActionModalDisableConfirmBtn : ""
        }
        confirmLabelTxtClass={
          !bulkActionEnable ? tblClasses.bulkActionModalDisableConfirmTxt : ""
        }
        cancelLabelBtnClass={
          !bulkActionEnable
            ? tblClasses.bulkActionModalDisableDiscardBtn
            : tblClasses.bulkActionModalEnableDiscardBtn
        }
        cancelLabelTxtClass={
          !bulkActionEnable ? "" : tblClasses.bulkActionModalEnableDiscardTxt
        }
        handleConfirm={() => handleBulkUpdateModalConfirm(bulkActionEnable)}
        cancelLabel="DISCARD"
        handleCancel={() => handleBulkUpdateModalOpen(false)}
      />
    </>
  );
};

export default ReminderList;
